import {
  createAction,
  createSelector,
  createSlice,
  PayloadAction
} from '@reduxjs/toolkit';
import { RootState } from 'app/store';
import { BudgetScenarioPlannerModel } from './models/budget-scenario-planner.model';
import { clearSites, selectSite } from 'features/topbar/selectedSiteSlice';
import { logout } from 'features/auth/authenticationSlice';
import { queryApi } from 'app/apis/queryApi';

export const BUDGET_ID_DELIMITER = '--';

export function createBudgetScenarioId(
  scenarioName: string,
  modelRunName: string,
  timeType: string,
  segmentKey: string
): string {
  return `${scenarioName}${BUDGET_ID_DELIMITER}${modelRunName}${BUDGET_ID_DELIMITER}${timeType}${BUDGET_ID_DELIMITER}${segmentKey}`;
}

export function createSystemBudgetScenarioId(
  scenarioName: string,
  modelRunName: string,
  timeType: string,
  segmentKey: string
): string {
  return `system${BUDGET_ID_DELIMITER}${scenarioName}${BUDGET_ID_DELIMITER}${modelRunName}${BUDGET_ID_DELIMITER}${timeType}${BUDGET_ID_DELIMITER}${segmentKey}`;
}

export function updateBudgetId(
  budgetId: string,
  scenarioName: string,
  modelRunName: string | undefined = undefined,
  timeType: string | undefined = undefined,
  segmentKey: string | undefined = undefined
): string {
  if (
    !budgetId ||
    (!scenarioName && !modelRunName && !timeType && !segmentKey)
  ) {
    throw new Error(
      'Invalid parameters for updating budget ID: Missing required fields.'
    );
  }

  const isSystemScenario = budgetId.startsWith('system-');

  const parts = budgetId.split('--');

  if (isSystemScenario) {
    parts.shift(); // Remove 'system-' prefix
  }

  if (scenarioName && scenarioName !== parts[0]) {
    parts[0] = scenarioName;
  }

  if (modelRunName && modelRunName !== parts[1]) {
    parts[1] = modelRunName;
  }

  if (timeType && timeType !== parts[2]) {
    parts[2] = timeType;
  }

  if (segmentKey && segmentKey !== parts[3]) {
    parts[3] = segmentKey;
  }

  return parts.join('--');
}

export function generateNewCustomScenarioName(
  numberOfScenarios: number,
  originalName: string
): string {
  return `Custom ${originalName} ${numberOfScenarios}`;
}

export function generateNewCopyScenarioName(
  numberOfScenarios: number,
  originalName: string
): string {
  return `${originalName} (Copy) ${numberOfScenarios}`;
}

export interface BudgetScenario {
  budgetId: string;
  scenarioName: string;
  originalScenarioName: string; // Optional field to store the original name
  modelRunName: string;
  timeType: string;
  segmentKey: string;
  updatedValues: {
    segmentValue: string;
    newSpendValue: number;
    newRevenueValue: number;
    newRoasValue: number;
    newMRoasValue: number;
  }[];
  hasNameChanged: boolean;
  isFirstCreation: boolean;
  // Temporary field to track if the scenario has been saved. In the future we will remove them from the slice once the API is updated.
  isSaved: boolean;
}

const editBudgetScenarioSlice = createSlice({
  name: 'EditBudgetScenario',
  initialState: {
    inProgressEditBudgetScenarios: [] as BudgetScenario[],
    availableSystemScenarios: [] as string[]
  },
  reducers: {
    upsertBudgetScenarioValues: (
      state,
      {
        payload
      }: PayloadAction<{
        scenarioName: string;
        modelRunName: string;
        timeType: string;
        segmentKey: string;
        segmentValue: string;
        newSpendValue: number;
        newRevenueValue: number;
        newRoasValue: number;
        newMRoasValue: number;
        availableSystemModels: string[];
      }>
    ) => {
      if (
        !payload.scenarioName ||
        !payload.modelRunName ||
        !payload.timeType ||
        !payload.segmentKey ||
        !payload.segmentValue ||
        payload.newSpendValue == null ||
        payload.newRevenueValue == null ||
        payload.newRoasValue == null ||
        payload.newMRoasValue == null
      ) {
        throw new Error(
          'Invalid payload for UpsertBudgetScenario: Missing required fields.'
        );
      }
      let budgetId = createBudgetScenarioId(
        payload.scenarioName,
        payload.modelRunName,
        payload.timeType,
        payload.segmentKey
      );

      const existingScenarioIndex =
        state.inProgressEditBudgetScenarios.findIndex(
          (scenario) => scenario.budgetId === budgetId
        );

      if (existingScenarioIndex !== -1) {
        const existingScenario =
          state.inProgressEditBudgetScenarios[existingScenarioIndex];

        existingScenario.isFirstCreation = false;

        existingScenario.modelRunName = payload.modelRunName;
        existingScenario.timeType = payload.timeType;
        existingScenario.segmentKey = payload.segmentKey;
        existingScenario.hasNameChanged =
          existingScenario.scenarioName !== payload.scenarioName;

        existingScenario.scenarioName = payload.scenarioName;

        existingScenario.updatedValues = [
          ...existingScenario.updatedValues.filter(
            (value) => value.segmentValue !== payload.segmentValue
          ),
          {
            segmentValue: payload.segmentValue,
            newSpendValue: payload.newSpendValue,
            newRevenueValue: payload.newRevenueValue,
            newRoasValue: payload.newRoasValue,
            newMRoasValue: payload.newMRoasValue
          }
        ];
      } else {
        let scenarioName = payload.scenarioName;

        if (payload.availableSystemModels.includes(payload.scenarioName)) {
          scenarioName = generateNewCustomScenarioName(
            state.inProgressEditBudgetScenarios.length + 1,
            payload.scenarioName
          );

          budgetId = createBudgetScenarioId(
            scenarioName,
            payload.modelRunName,
            payload.timeType,
            payload.segmentKey
          );
        }

        const newScenario: BudgetScenario = {
          budgetId,
          scenarioName,
          modelRunName: payload.modelRunName,
          timeType: payload.timeType,
          segmentKey: payload.segmentKey,
          originalScenarioName: payload.scenarioName,
          updatedValues: [
            {
              segmentValue: payload.segmentValue,
              newSpendValue: payload.newSpendValue,
              newRevenueValue: payload.newRevenueValue,
              newRoasValue: payload.newRoasValue,
              newMRoasValue: payload.newMRoasValue
            }
          ],
          hasNameChanged: false,
          isFirstCreation: true,
          isSaved: false
        };

        state.inProgressEditBudgetScenarios.push(newScenario);
      }
    },
    copyBudgetScenario: (
      state,
      {
        payload
      }: PayloadAction<{
        budgetId: string;
      }>
    ) => {
      if (payload.budgetId.startsWith('system-')) {
        const budgetIdParts = payload.budgetId.split('--');
        budgetIdParts.shift(); // Remove 'system-' prefix
        const scenarioName = budgetIdParts[0];
        const modelRunName = budgetIdParts[1];
        const timeType = budgetIdParts[2];
        const segmentKey = budgetIdParts[3];

        const newBudgetScenarioName = generateNewCopyScenarioName(
          state.inProgressEditBudgetScenarios.length + 1,
          scenarioName
        );

        const newBudgetId = updateBudgetId(
          payload.budgetId,
          newBudgetScenarioName,
          modelRunName,
          timeType,
          segmentKey
        );

        const newScenario: BudgetScenario = {
          budgetId: newBudgetId,
          scenarioName: newBudgetScenarioName,
          originalScenarioName: scenarioName,
          modelRunName,
          timeType,
          segmentKey,
          updatedValues: [],
          hasNameChanged: false,
          isFirstCreation: true,
          isSaved: false
        };

        state.inProgressEditBudgetScenarios.push(newScenario);
      } else {
        const existingCustomScenarioIndex =
          state.inProgressEditBudgetScenarios.findIndex(
            (scenario) => scenario.budgetId === payload.budgetId
          );
        if (existingCustomScenarioIndex === -1) {
          return;
        }
        if (existingCustomScenarioIndex !== -1) {
          const existingScenario =
            state.inProgressEditBudgetScenarios[existingCustomScenarioIndex];

          const newBudgetScenarioName = generateNewCopyScenarioName(
            state.inProgressEditBudgetScenarios.length + 1,
            existingScenario.scenarioName
          );

          const newBudgetId = updateBudgetId(
            payload.budgetId,
            newBudgetScenarioName
          );

          const newScenario: BudgetScenario = {
            ...existingScenario,
            budgetId: newBudgetId,
            scenarioName: newBudgetScenarioName,
            hasNameChanged: false,
            isFirstCreation: true
          };

          state.inProgressEditBudgetScenarios.push(newScenario);
        }
      }
    },
    changeBudgetScenarioName: (
      state,
      {
        payload
      }: PayloadAction<{
        budgetId: string;
        scenarioName: string;
      }>
    ) => {
      const existingScenarioIndex =
        state.inProgressEditBudgetScenarios.findIndex(
          (scenario) => scenario.budgetId === payload.budgetId
        );

      if (existingScenarioIndex === -1) {
        return;
      }

      const newBudgetId = updateBudgetId(
        payload.budgetId,
        payload.scenarioName
      );

      const existingScenario =
        state.inProgressEditBudgetScenarios[existingScenarioIndex];

      existingScenario.budgetId = newBudgetId;
      existingScenario.scenarioName = payload.scenarioName;
      existingScenario.hasNameChanged = true;
    },
    deleteBudgetScenario: (
      state,
      {
        payload
      }: PayloadAction<{
        budgetId: string;
      }>
    ) => {
      const existingScenarioIndex =
        state.inProgressEditBudgetScenarios.findIndex(
          (scenario) => scenario.budgetId === payload.budgetId
        );

      if (existingScenarioIndex !== -1) {
        state.inProgressEditBudgetScenarios.splice(existingScenarioIndex, 1);
      } else {
        console.warn(`No scenario found with budgetId: ${payload.budgetId}`);
      }
    },
    markBudgetScenarioAsSaved: (
      state,
      {
        payload
      }: PayloadAction<{
        budgetId: string;
      }>
    ) => {
      const existingScenarioIndex =
        state.inProgressEditBudgetScenarios.findIndex(
          (scenario) => scenario.budgetId === payload.budgetId
        );

      if (existingScenarioIndex !== -1) {
        state.inProgressEditBudgetScenarios[existingScenarioIndex].isSaved =
          true;
      } else {
        console.warn(`No scenario found with budgetId: ${payload.budgetId}`);
      }
    }
  },
  extraReducers: (builder) => {
    builder.addCase('PURGE', () => {
      return {
        inProgressEditBudgetScenarios: [],
        availableSystemScenarios: []
      };
    });
    builder.addCase(logout, () => {
      return {
        inProgressEditBudgetScenarios: [],
        availableSystemScenarios: []
      };
    });
    builder.addCase(selectSite, () => {
      return {
        inProgressEditBudgetScenarios: [],
        availableSystemScenarios: []
      };
    });
    builder.addCase(clearSites, () => {
      return {
        inProgressEditBudgetScenarios: [],
        availableSystemScenarios: []
      };
    });
    builder.addMatcher(
      queryApi.endpoints.getDataScienceMmmConfig.matchFulfilled,
      (state, { payload }) => {
        if (!payload) {
          return;
        }

        state.availableSystemScenarios = payload.flatMap(
          (data) => data.availableModelTypes
        );
      }
    );
  }
});

interface SaveInEditModeBudgetScenarioPayload {
  budgetId: string;
  scenarioName: string;
}

export const saveInEditModeBudgetScenario =
  createAction<SaveInEditModeBudgetScenarioPayload>(
    'editBudgetScenario/saveInEditModeBudgetScenario'
  );

export const {
  upsertBudgetScenarioValues,
  changeBudgetScenarioName,
  copyBudgetScenario,
  deleteBudgetScenario,
  markBudgetScenarioAsSaved
} = editBudgetScenarioSlice.actions;

export const editBudgetScenarioReducer = editBudgetScenarioSlice.reducer;

export const selectIsEditingCurrentSelectedBudgetScenario = createSelector(
  (state: RootState) => state.editBudgetScenario,
  (
    _: RootState,
    selectedBudgetScenario:
      | {
          budgetId: string;
          scenarioName: string;
        }
      | undefined
  ) => selectedBudgetScenario,
  (state, selectedBudgetScenario) => {
    if (!selectedBudgetScenario) {
      return false;
    }

    return state.inProgressEditBudgetScenarios.some(
      (scenario) =>
        scenario.budgetId === selectedBudgetScenario.budgetId &&
        scenario.scenarioName === selectedBudgetScenario.scenarioName
    );
  }
);

export const selectUpdatedBudgetScenarios = createSelector(
  (state: RootState) => state.editBudgetScenario,
  (
    state: RootState,
    selectedBudgetScenario:
      | {
          budgetId: string;
          scenarioName: string;
        }
      | undefined
  ) => selectedBudgetScenario,
  (state, selectedBudgetScenario) => {
    if (!selectedBudgetScenario) {
      return undefined;
    }

    return state.inProgressEditBudgetScenarios.find(
      (scenario) =>
        scenario.budgetId === selectedBudgetScenario.budgetId &&
        scenario.scenarioName === selectedBudgetScenario.scenarioName
    );
  }
);

export const selectAllUpdatedBudgetScenario = createSelector(
  (state: RootState) => state.editBudgetScenario,
  (state) => {
    return state.inProgressEditBudgetScenarios.map((scenario) => {
      return {
        budgetId: scenario.budgetId,
        scenarioName: scenario.scenarioName,
        modelRunName: scenario.modelRunName,
        scenarioType: 'Custom',
        isSaved: scenario.isSaved
      } as BudgetScenarioPlannerModel;
    });
  }
);

export const selectHaveAnyBudgetScenariosWhereNameHasNotBeenChanged =
  createSelector(
    (state: RootState) => state.editBudgetScenario,
    (state) => {
      return state.inProgressEditBudgetScenarios.some(
        (scenario) => !scenario.hasNameChanged
      );
    }
  );

export const selectDoesCurrentBudgetScenarioHaveUnchangedName = createSelector(
  (state: RootState) => state.editBudgetScenario,
  (
    _: RootState,
    selectedBudgetScenario:
      | {
          budgetId: string;
          scenarioName: string;
        }
      | undefined
  ) => selectedBudgetScenario,
  (state, selectedBudgetScenario) => {
    if (!selectedBudgetScenario) {
      return false;
    }

    const scenario = state.inProgressEditBudgetScenarios.find(
      (s) =>
        s.budgetId === selectedBudgetScenario.budgetId &&
        s.scenarioName === selectedBudgetScenario.scenarioName
    );

    return scenario ? !scenario.hasNameChanged : false;
  }
);
